import React, { useState } from 'react';
import api from '../services/api';

const SimulationModal = ({ eventId, eventName, onClose, onResults }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const runSimulation = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/optimization/forecast', { eventId });
      setResults(res.data);
      onResults?.(res.data);
    } catch (err) {
      console.error(err);
      setError('Simulation failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
      const badgeStyles = {
          winning: { bg: '#163321', color: '#F59E0B', border: '#163321', label: 'Likely to Win' },
          marginal_winning: { bg: '#163321', color: '#F59E0B', border: '#F59E0B', label: 'Marginal (winning)' },
          marginal_losing: { bg: '#F8FAFC', color: '#0D9488', border: '#0D9488', label: 'Marginal (losing)' },
          losing: { bg: '#0D9488', color: '#F59E0B', border: '#0D9488', label: 'Unlikely to Win' }
      };
      const s = badgeStyles[status] || badgeStyles.losing;

      return (
          <span style={{
              backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}`,
              padding: '4px 10px', borderRadius: '12px', fontWeight: 'bold', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif'
          }}>
              {s.label}
          </span>
      );
  };

  // Field friendly name (customName), with the RPA parcel code alongside it
  // when configured — falls back to just the code when no custom name is set.
  const getFieldLabel = (res) => res.parcelCustomName ? `${res.parcelCustomName} (${res.parcelRef})` : res.parcelRef;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
            <h3 style={{ margin: 0, fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: '600', color: '#0F172A' }}>Check Your Odds</h3>
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.body}>
            <p style={{marginTop:0, color:'#475569', fontFamily: '"Inter", sans-serif'}}>
                Running a simulation for <strong style={{color: '#0F172A'}}>{eventName}</strong> compares your current bids against the estimated market competition.
            </p>

            {!results && !loading && (
                <div style={{textAlign:'center', padding:'30px', backgroundColor:'#F8FAFC', borderRadius:'8px', border:'1px dashed #ddd'}}>
                    <p style={{color:'#475569', fontFamily: '"Inter", sans-serif'}}>Ready to forecast?</p>
                    <button onClick={runSimulation} style={styles.runBtn}>
                        Run Prediction
                    </button>
                </div>
            )}

            {loading && (
                <div style={{textAlign:'center', padding:'40px'}}>
                    <div style={styles.spinner}></div>
                    <p style={{marginTop:'15px', color:'#475569', fontFamily: '"Inter", sans-serif'}}>Running algorithm against live market data...</p>
                </div>
            )}

            {results && (
                <div style={{ fontFamily: '"Inter", sans-serif' }}>
                    <div style={{marginBottom:'15px', padding:'10px', backgroundColor:'#163321', borderRadius:'4px', color:'#F8FAFC', fontSize:'0.9rem'}}>
                        <strong>Note:</strong> This is a live estimate based on current competition. Final results may change as new bids arrive.
                    </div>
                    {results.length === 0 ? (
                        <div style={{padding:'20px', textAlign:'center', color:'#777'}}>No active bids found for this event.</div>
                    ) : (
                        <div style={{maxHeight:'300px', overflowY:'auto'}}>
                            <table style={{width:'100%', borderCollapse:'collapse'}}>
                                <thead>
                                    <tr style={{textAlign:'left', borderBottom:'2px solid #eee', color:'#888', fontSize:'0.85rem'}}>
                                        <th style={{padding:'8px'}}>Field</th>
                                        <th style={{padding:'8px'}}>Action</th>
                                        <th style={{padding:'8px'}}>Price per Unit</th>
                                        <th style={{padding:'8px'}}>Overall Price</th>
                                        <th style={{padding:'8px'}}>Forecast</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((res, idx) => {
                                        const pricePerUnit = res.quantity > 0 ? res.price / res.quantity : null;
                                        return (
                                        <tr key={idx} style={{borderBottom:'1px solid #f0f0f0'}}>
                                            <td style={{padding:'10px', fontWeight:'bold'}}>{getFieldLabel(res)}</td>
                                            <td style={{padding:'10px', fontSize:'0.9rem'}}>
                                                {res.action}<br/>
                                                <span style={{fontSize:'0.75rem', color:'#999'}}>
                                                    BCR: {res.benefitCostRatio > 0 ? `${res.benefitCostRatio.toLocaleString('en-GB', {maximumFractionDigits: 2})}:1` : '0:1'}
                                                </span>
                                            </td>
                                            <td style={{padding:'10px'}}>{pricePerUnit !== null ? `£${pricePerUnit.toLocaleString('en-GB', {maximumFractionDigits: 2})}` : '-'}</td>
                                            <td style={{padding:'10px'}}>£{res.price}</td>
                                            <td style={{padding:'10px'}}>
                                                {getStatusBadge(res.status)}
                                                {res.status !== 'winning' && (
                                                    <div style={{fontSize:'0.75rem', color:'#666', marginTop:'4px'}}>
                                                        {res.reason}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div style={{textAlign:'right', marginTop:'20px'}}>
                        <button onClick={onClose} style={styles.secondaryBtn}>Done</button>
                    </div>
                </div>
            )}
            {error && <div style={{marginTop:'15px', color:'red', textAlign:'center'}}>{error}</div>}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15, 23, 42, 0.5)', zIndex: 11000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  modal: { backgroundColor: '#FFFFFF', width: '90%', maxWidth: '700px', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden' },
  header: { padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' },
  body: { padding: '20px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#475569' },
  runBtn: { padding: '12px 24px', backgroundColor: '#0D9488', color: '#FFFFFF', border: 'none', borderRadius: '4px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', fontFamily: '"Inter", sans-serif' },
  secondaryBtn: { padding: '10px 20px', backgroundColor: '#0D9488', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' },
  spinner: { border: '4px solid #f3f3f3', borderTop: '4px solid #0D9488', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto' }
};

if (!document.getElementById('spinner-style')) {
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-style';
    styleSheet.innerText = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
    document.head.appendChild(styleSheet);
}

export default SimulationModal;